/**
 * Recent Activity home-page widget (L-102, Daniel 2026-07-08).
 *
 * Two-column grid: "Last 24 hours" | "Last 7 days". Five stat rows in
 * each: emails sent, texts sent, tasks completed, new leads, deals
 * touched. Team-wide counts (not per-agent) so Daniel's Monday-morning
 * "did we do anything?" pulse covers everyone at once.
 *
 * Card hides itself when both windows are all-zero (fresh team, or a
 * quiet stretch). No point rendering "0 0 0 0 0 | 0 0 0 0 0".
 */
import { useQuery } from '@tanstack/react-query';
import { getRecentActivity, type RecentActivityWindow } from '../api/home';

const STAT_ROWS: Array<{ key: keyof RecentActivityWindow; label: string }> = [
  { key: 'emails_sent',     label: 'Emails sent' },
  { key: 'texts_sent',      label: 'Texts sent' },
  { key: 'tasks_completed', label: 'Tasks completed' },
  { key: 'new_leads',       label: 'New leads' },
  { key: 'deals_touched',   label: 'Deals touched' },
];

export default function RecentActivityCard() {
  const q = useQuery({
    queryKey: ['recent-activity'],
    queryFn: getRecentActivity,
    staleTime: 60_000,
  });

  if (q.isLoading || !q.data) return null;

  const totalWeek = STAT_ROWS.reduce((n, r) => n + (q.data!.week[r.key] ?? 0), 0);
  if (totalWeek === 0) return null; // quiet stretch — collapse the widget entirely

  return (
    <section style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      padding: '18px 22px',
      marginBottom: 20,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-heading)', margin: 0 }}>
          Recent activity
        </h2>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Team-wide</span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 14,
      }}>
        <WindowColumn label="Last 24 hours" window={q.data.day} />
        <WindowColumn label="Last 7 days" window={q.data.week} />
      </div>
    </section>
  );
}

function WindowColumn({ label, window }: { label: string; window: RecentActivityWindow }) {
  return (
    <div style={{
      background: 'var(--bg-surface-2, #F5F5F7)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
      }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {STAT_ROWS.map((row) => {
          const n = window[row.key] ?? 0;
          return (
            <div key={row.key} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              fontSize: 13,
            }}>
              <span style={{ color: n > 0 ? 'var(--text-body)' : 'var(--text-muted)' }}>{row.label}</span>
              <span style={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: n > 0 ? 600 : 400,
                color: n > 0 ? 'var(--text-heading)' : 'var(--text-muted)',
              }}>{n.toLocaleString()}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
